import React, { useState, useEffect } from "react";
import {
    userInsuranceServiceList,
    insuranceFileNy,
    nyIdfServiceList,
    expenseReimbursement25K,
    expenseReimbursement1M,
    expenseReimbursement2M,
} from "../../config/UserServicesList";
import { useTranslation } from "react-i18next";

interface IProps {
    userServices: any;
    userState: string;
    userCountry: string;
    isNYStateUser: boolean;
    isIDFNYPackage: boolean;
    isIDFPartner: boolean;
    isAvastId: boolean;
    isAVGId: boolean;
    isAVGTestId: boolean;
    isInsuranceLoading: boolean;
    insuranceMembership: any;
    currentLanguage: string;
    getGeneratedPdf: any;
}

interface ServiceObject {
    serviceCode: string;
    serviceDescription: string;
    dollarAmount: string;
    insuranceDocument: string;
    swissReUSDocument: string;
    swissReCADocument: string;
    swissReCAFRDocument: string;
    aigDocument?: string;
    assurantDocument?: string;
}

const Insurance = ({
    userServices,
    userState,
    userCountry,
    isNYStateUser,
    isIDFNYPackage,
    isIDFPartner,
    isAvastId,
    isAVGId,
    isAVGTestId,
    isInsuranceLoading,
    insuranceMembership,
    currentLanguage,
    getGeneratedPdf,
}: IProps) => {
    const { t: tLang } = useTranslation();

    const [activeServices, setActiveServices] = useState<ServiceObject[]>([]);
    const [packageType, setPackageType] = useState("");
    const [insuranceDocument, setInsuranceDocument] = useState("");
    const [insuranceAmount, setInsuranceAmount] = useState("");

    const lloyds = ["SVC-EXPINS", "SVC-EXPINS-25k", "SVC-EXPINS-1M", "SVC-EXPINS-2M"];
    const endorsements = ["SVC-RNSM-REIMB", "SVC-SOCENG-REIMB", "SVC-CB-25K", "SVC-CB-50K"];
    const aig = [
        "SVCLVL-IDR-IDR-ITI2P",
        "SVCLVL-IDR-FR-ITI2P",
        "SVCLVL-FIDR25-UEFT",
        "SVCLVL-IDR-IND-1MM",
        "SVCLVL-IDR-FAM-1MM",
        "SVC-ITI-FAM-25UEFT-NY",
        "SVC-ITI-FAM-1M-NY",
    ];
    const assurant = [
        "SVC-401K-FAM-1MUEFT-NC",
        "SVC-401K-FAM-CYB-1MUEFT-NC",
        "SVC-401K-FAM-2MUEFT-NC",
        "SVC-401K-FAM-CYB-2MUEFT-NC",
        "SVC-IDFraud-25K-NC",
        "SVC-IDFraud-FAM-2M-TRIO-NY",
        "SVC-IDFraud-FAM-1M-TRIO-NY",
        "SVC-IDFraud-FAM-2M-NC",
        "SVC-IDFraud-FAM-2MUEFT-NC",
        "SVC-IDFraud-FAM-5M-NC",
        "SVC-IDFraud-FAM-5MUEFT-NC",
        "SVC-IDFraud-5M-NC",
        "SVC-IDFraud-5MUEFT-NC",
        "SVC-IDFraud-1M-NC",
        "SVC-IDFraud-1MUEFT-NC",
        "SVC-IDFraud-25KUEFT-NC",
        "SVC-IDFraud-50K-NC",
        "SVC-IDFraud-FAM-2MNC-TRIO-NY",
        "SVC-IDFraud-FAM-1MNC-TRIO-NY",
    ];
    const combinedList = [...lloyds, ...aig, ...assurant];

    const getActiveInsuranceServices = () => {
        if (userServices && userServices.length > 0) {
            let insuranceServices: {
                serviceCode: string;
                serviceDescription: string;
                dollarAmount: string;
                insuranceDocument: string;
                swissReUSDocument: string;
                swissReCADocument: string;
                swissReCAFRDocument: string;
                aigDocument?: string;
                assurantDocument?: string;
            }[] = [];

            userServices.forEach((service: any) => {
                const matches = userInsuranceServiceList.filter(
                    (insurance) => insurance.serviceCode.toLowerCase() === service.serviceCode.toLowerCase() && service.active
                );

                matches.forEach((matchedService) => {
                    if (insuranceServices.length === 0) {
                        insuranceServices = [...insuranceServices, matchedService];
                    } else {
                        // prevent duplicate service codes by comparing with the first element
                        if (!insuranceServices.some((existingService) => existingService.serviceCode === matchedService.serviceCode)) {
                            insuranceServices = [...insuranceServices, matchedService];
                        }
                    }
                });
            });

            if (insuranceServices.length > 0) {
                setActiveServices(insuranceServices);
            }
        }
    };

    const getInsurancePackageDetails = () => {
        const matches = activeServices.filter((service) => combinedList.includes(service.serviceCode));
        const matchCount = {
            lloyds: matches.filter((service) => lloyds.includes(service.serviceCode)).length,
            aig: matches.filter((service) => aig.includes(service.serviceCode)).length,
            assurant: matches.filter((service) => assurant.includes(service.serviceCode)).length,
            endorsements: activeServices.filter((service) => endorsements.includes(service.serviceCode)).length, // endorsements aren't included in combinedList
        };

        if (matchCount.lloyds > 0) {
            if (matchCount.aig > 0) {
                setPackageType("combinationAIG"); // lloyds + aig
            } else if (matchCount.assurant > 0) {
                setPackageType("combinationAssurant"); // lloyds + assurant
            } else if (matchCount.endorsements > 0) {
                setPackageType("combinationEndorsement");
            } else {
                setPackageType("Lloyds");
            }
        } else if (matchCount.aig > 0) {
            setPackageType("AIG");
        } else if (matchCount.assurant > 0) {
            setPackageType("Assurant");
        }
    };

    const getProviderByRegion = () => {
        if ((userState === "NY" && userCountry === "US") || isNYStateUser) {
            getDocumentByProvider(packageType === "combinationAssurant" || packageType === "Assurant" ? "Assurant" : "AIG");
        } else if (userCountry === "US" || !userCountry) {
            getDocumentByProvider("SwissReUS");
        } else if (userCountry === "CA") {
            getDocumentByProvider("SwissReCA");
        } else {
            getDocumentByProvider("Lloyds");
        }
    };

    const getServiceObject = (provider: string) => {
        let filteredService = activeServices.filter((service) => {
            if (activeServices.length > 1) {
                if (packageType === "combinationEndorsement") {
                    return lloyds.includes(service.serviceCode);
                } else {
                    if (packageType === "Assurant" || (packageType === "combinationAssurant" && provider === "Assurant")) {
                        return assurant.includes(service.serviceCode);
                    } else if (packageType === "AIG" || (packageType === "combinationAIG" && provider === "AIG")) {
                        return aig.includes(service.serviceCode);
                        // if there are multiple service codes in the package, SwissRe pdf will always be tied to lloyds service code
                    } else if (provider === "Lloyds" || provider === "SwissReUS" || provider === "SwissReCA") {
                        return lloyds.includes(service.serviceCode);
                    } else {
                        return true;
                    }
                }
            } else {
                return true;
            }
        });

        // for endorsement packages, pull document from endorsement specific object based on EXPINS variation
        if (packageType === "combinationEndorsement" || (packageType.includes("combinationA") && userState !== "NY")) {
            const priorityOrder = ["SVC-CB-50K", "SVC-CB-25K", "SVC-SOCENG-REIMB", "SVC-RNSM-REIMB"];
            const endorsementListByPriority = activeServices
                .filter((service) => endorsements.includes(service.serviceCode))
                .sort((a, b) => {
                    return priorityOrder.indexOf(a.serviceCode) - priorityOrder.indexOf(b.serviceCode);
                });

            if (filteredService.length === 1 && endorsementListByPriority.length > 0) {
                // Get the highest priority endorsement
                const highestPriorityEndorsement = endorsementListByPriority[0].serviceCode;

                // Check if we have the specific combination of SVC-SOCENG-REIMB and SVC-RNSM-REIMB as the highest priorities
                // This will only be true if no CB variants are present (since they have higher priority)
                const hasSocialEngineering = highestPriorityEndorsement === "SVC-SOCENG-REIMB";
                const hasRansomware = endorsementListByPriority.length > 1 && endorsementListByPriority[1].serviceCode === "SVC-RNSM-REIMB";
                const useSpecialCombination = hasSocialEngineering && hasRansomware;

                // Determine which service code to use for lookup
                const lookupServiceCode = useSpecialCombination ? "SVC-SOCENG-REIMB+SVC-RNSM-REIMB" : highestPriorityEndorsement;

                if (filteredService[0].serviceCode.toUpperCase() === "SVC-EXPINS-25K") {
                    const matchedObject = expenseReimbursement25K.find((item) => item.serviceCode === lookupServiceCode);
                    filteredService = matchedObject ? [matchedObject] : filteredService;
                } else if (filteredService[0].serviceCode.toUpperCase() === "SVC-EXPINS-1M") {
                    const matchedObject = expenseReimbursement1M.find((item) => item.serviceCode === lookupServiceCode);
                    filteredService = matchedObject ? [matchedObject] : filteredService;
                } else if (filteredService[0].serviceCode.toUpperCase() === "SVC-EXPINS-2M") {
                    const matchedObject = expenseReimbursement2M.find((item) => item.serviceCode === lookupServiceCode);
                    filteredService = matchedObject ? [matchedObject] : filteredService;
                }
            }
        } else if (isIDFNYPackage && isNYStateUser) {
            const matchedObject = nyIdfServiceList.find((item) => item.serviceCode === filteredService[0].serviceCode);
            filteredService = matchedObject ? [matchedObject] : filteredService;
        }

        return filteredService[0];
    };

    const getDocumentByProvider = (provider: string) => {
        // selects which service object to use when fetching document path
        const selectedService = getServiceObject(provider);
        let insuranceDocument = "";

        if (provider === "AIG") {
            insuranceDocument = selectedService.aigDocument ? selectedService.aigDocument : selectedService.insuranceDocument;
        } else if (provider === "Assurant") {
            insuranceDocument = selectedService.assurantDocument ? selectedService.assurantDocument : selectedService.insuranceDocument;
        } else if (provider === "SwissReUS") {
            insuranceDocument = selectedService.swissReUSDocument;
        } else if (provider === "SwissReCA") {
            insuranceDocument = currentLanguage == "fr" ? selectedService.swissReCAFRDocument : selectedService.swissReCADocument;
        } else {
            insuranceDocument = selectedService.swissReUSDocument ? selectedService.swissReUSDocument : selectedService.insuranceDocument;
        }

        setInsuranceAmount(selectedService.dollarAmount);
        setInsuranceDocument(insuranceDocument);
    };

    useEffect(() => {
        getActiveInsuranceServices();
    }, []);

    useEffect(() => {
        if (activeServices.length > 0) {
            getInsurancePackageDetails();
        }
    }, [activeServices]);

    useEffect(() => {
        if (packageType) {
            getProviderByRegion();
        }
    }, [packageType]);

    return (
        <div className={isIDFPartner ? "box box-border-top" : "box"}>
            <div className="box-header">
                <div className="box-title-container">
                    <div className="box-title-text">
                        <h2 className="box-title" id="credit-score-trend-title">
                            {isAvastId || isAVGId || isAVGTestId ? "Expense Reimbursement " : tLang("supportSection.insuranceTitleText")}
                        </h2>
                    </div>
                </div>
                <div className="box-description"></div>
                <div className="box-actions"></div>
            </div>
            {isInsuranceLoading ? (
                <div className="box-content loading"></div>
            ) : (
                <div className="box-content">
                    <p>
                        {tLang("supportSection.generalSupportWidget.insuranceContent.message.initialText")} {insuranceAmount} {insuranceMembership}{" "}
                        {tLang("supportSection.generalSupportWidget.insuranceContent.message.middleText")}
                        {isAvastId || isAVGId || isAVGTestId ? "reimbursement " : " insurance"}{" "}
                        {tLang("supportSection.generalSupportWidget.insuranceContent.message.lastText")}
                    </p>

                    <p id="footnote-label">
                        <small id="footnotes">
                            {tLang("supportSection.generalSupportWidget.insuranceContent.notification.initialText")}{" "}
                            {isAvastId || isAVGId || isAVGTestId ? "reimbursement " : "insurance"}{" "}
                            {tLang("supportSection.generalSupportWidget.insuranceContent.notification.lastText")}
                        </small>
                    </p>

                    <p>
                        {tLang("supportSection.generalSupportWidget.insuranceContent.link.content")}{" "}
                        <a
                            type="button"
                            target="_blank"
                            className="default_link"
                            onClick={(e) => {
                                if (insuranceDocument && insuranceFileNy.includes(insuranceDocument)) {
                                    e.preventDefault();
                                    getGeneratedPdf(insuranceDocument);
                                }
                            }}
                            href={"doc/" + insuranceDocument}
                            rel="noreferrer"
                        >
                            <span className="btn-text">{tLang("supportSection.generalSupportWidget.insuranceContent.link.buttonText")}</span>
                        </a>
                    </p>
                </div>
            )}
        </div>
    );
};

export default Insurance;
